import React, { useState, useEffect } from "react";
import { X, Share, Menu, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

// Thêm prop forceShow để có thể hiển thị bằng tay khi cần
interface PWAInstallPromptProps {
  forceShow?: boolean;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ forceShow = false }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | "other">("other");
  const { t } = useTranslation();

  // Hàm reset trạng thái đã hiển thị
  const resetPromptState = () => {
    localStorage.removeItem("hasShownPWAPrompt");
  };

  // Kiểm tra thiết bị di động đơn giản
  const checkIsMobile = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  useEffect(() => {
    // Kiểm tra xem đã hiển thị prompt này chưa
    const hasShownPWAPrompt = localStorage.getItem("hasShownPWAPrompt");
    
    // Kiểm tra xem URL có tham số force_pwa_prompt không
    const urlParams = new URLSearchParams(window.location.search);
    const isForcedPrompt = urlParams.has('force_pwa_prompt');
    
    // Kiểm tra xem người dùng đang sử dụng thiết bị di động không (đơn giản hóa)
    const isMobile = checkIsMobile();
    
    // Xác định platform
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (isIOS) {
      setPlatform("ios");
      document.documentElement.classList.add("ios");
    } else if (isAndroid) {
      setPlatform("android");
      document.documentElement.classList.add("android");
    }
    
    // Kiểm tra người dùng đã cài đặt dưới dạng PWA chưa
    const isRunningAsPWA = window.matchMedia('(display-mode: standalone)').matches || 
                          (window.navigator as any).standalone === true;

    // Xác định xem có hiển thị prompt hay không - đã đơn giản hóa logic
    // Hiển thị prompt nếu người dùng đang dùng thiết bị di động và KHÔNG chạy dưới dạng PWA
    const shouldShowPrompt = forceShow || 
                            isForcedPrompt || 
                            (isMobile && !isRunningAsPWA);
    
    if (shouldShowPrompt) {
      // Đợi 2s sau khi trang đã load để không làm phiền người dùng ngay lập tức
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleClose = () => {
    setShowPrompt(false);
    // Đánh dấu rằng đã hiển thị prompt
    localStorage.setItem("hasShownPWAPrompt", "true");
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 p-4 shadow-lg z-50 border-t border-gray-200 dark:border-gray-700 pwa-install-prompt">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("pwaPrompt.title", "Tạo phím tắt trang web")}
        </h3>
        <button 
          onClick={handleClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X size={20} />
        </button>
      </div>
      
      <p className="text-gray-600 dark:text-gray-300 mb-3">
        {t("pwaPrompt.description", "Truy cập Giác Ngộ nhanh chóng như một ứng dụng trên màn hình chính của bạn")}
      </p>
      
      {platform === "ios" && (
        <div className="pwa-ios-instructions">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center">
                <Share size={16} />
              </div>
              <p className="text-sm">{t("pwaPrompt.ios.step1", "Nhấn vào biểu tượng Chia sẻ")}</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center">
                <Plus size={16} />
              </div>
              <p className="text-sm">{t("pwaPrompt.ios.step2", "Cuộn xuống và chạm vào \"Thêm vào màn hình chính\"")}</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>
              </div>
              <p className="text-sm">{t("pwaPrompt.ios.step3", "Nhấn \"Thêm\" ở góc trên bên phải")}</p>
            </div>
          </div>
        </div>
      )}
      
      {platform === "android" && (
        <div className="pwa-android-instructions">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center">
                <Menu size={16} />
              </div>
              <p className="text-sm">{t("pwaPrompt.android.step1", "Nhấn vào biểu tượng Menu (ba chấm)")}</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><path d="M12 8v8"></path><path d="M8 12h8"></path></svg>
              </div>
              <p className="text-sm">{t("pwaPrompt.android.step2", "Chọn \"Cài đặt trang web này\"")}</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12"></path><path d="m8 11 4 4 4-4"></path><path d="M8 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4"></path></svg>
              </div>
              <p className="text-sm">{t("pwaPrompt.android.step3", "Nhấn \"Cài đặt\"")}</p>
            </div>
          </div>
        </div>
      )}
      
      {platform === "other" && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t("pwaPrompt.generic", "Thêm trang web này vào màn hình chính từ menu trình duyệt của bạn.")}
        </p>
      )}
      
      <div className="mt-4 flex justify-end">
        <button
          onClick={handleClose}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t("pwaPrompt.dismiss", "Đã hiểu")}
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt; 